import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Select from './Select';

// Reusable client-side pagination control: "X–Y of Z" range, prev/next
// (disabled at the bounds), and a page-size selector. Page indices are
// 1-based. The caller owns the slice and the state; this is presentation
// only.

interface PaginationProps {
    /** Current page, 1-based. */
    page: number;
    pageSize: number;
    /** Total number of rows across all pages. */
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    pageSizeOptions?: ReadonlyArray<number>;
    className?: string;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250] as const;

const formatCount = (n: number) => n.toLocaleString('en-GB');

export default function Pagination({
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    className,
}: PaginationProps) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const clampedPage = Math.min(page, totalPages);
    const first = total === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
    const last = Math.min(clampedPage * pageSize, total);
    const atStart = clampedPage <= 1;
    const atEnd = clampedPage >= totalPages;

    // Each button is 24px square, the smallest click target that we allow.
    const navButton =
        'flex items-center justify-center w-6 h-6 rounded-button ' +
        'text-[rgb(var(--color-text-secondary))] transition-colors ' +
        'hover:bg-[rgb(var(--color-surface-secondary))] ' +
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';

    return (
        // 4px above and below the 24px controls, so the select does not
        // touch the border of the table or the edge of the card.
        <div
            className={cn(
                'flex min-h-8 items-center justify-between gap-3 px-1.5 py-1 border-t border-[rgb(var(--color-divider))]',
                className,
            )}
        >
            <div className="flex items-center gap-1.5">
                <span className="text-caption text-[rgb(var(--color-text-tertiary))]">
                    Rows per page
                </span>
                <Select
                    size="sm"
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    aria-label="Rows per page"
                    className="font-mono tabular-nums"
                >
                    {pageSizeOptions.map((size) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                    ))}
                </Select>
            </div>

            <div className="flex items-center gap-2">
                <span className="text-caption text-[rgb(var(--color-text-secondary))] font-mono tabular-nums">
                    {formatCount(first)}–{formatCount(last)} of {formatCount(total)}
                </span>
                <div className="flex items-center">
                    <button
                        type="button"
                        aria-label="Previous page"
                        className={navButton}
                        disabled={atStart}
                        onClick={() => onPageChange(clampedPage - 1)}
                    >
                        <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        aria-label="Next page"
                        className={navButton}
                        disabled={atEnd}
                        onClick={() => onPageChange(clampedPage + 1)}
                    >
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
